import React, { useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  Button,
  notification,
  Select,
  ColorPicker,
} from "antd";
import { useMutation } from "@tanstack/react-query";
import { createPremiumProject, updateProject } from "../services/projectsAPI";
import { fetchCurrentUser } from "../services/authAPI";
import { uploadFile } from "../services/filesAPI";
import { useAuth } from "../hooks/useAuth";
import queryClient from "../queryClient";
import {
  getDefaultCompanyLogo,
  normalizeBooleanValue,
} from "../utils/projectUtils";
import AttachmentsSection from "./AttachmentsSection";
import ProjectAddressFormFields from "./ProjectAddressFormFields";
import ProjectSettingToggle from "./ProjectSettingToggle";
import {
  formatPhoneNumber,
  handlePhoneNumberKeyDown,
} from "../utils/textUtils";

const CreatePremiumProjectModal = ({ visible, onClose }) => {
  const [form] = Form.useForm();
  const { currentUser, selectedProject, setCurrentUser, setSelectedProject } =
    useAuth();

  const createPremiumProjectMutation = useMutation({
    mutationFn: async (values) => {
      const { company_logo, ...projectData } = values;
      let logoFilesToUpload = [];
      let existingLogoFiles = [];

      if (
        company_logo &&
        Array.isArray(company_logo) &&
        company_logo.length > 0
      ) {
        company_logo.forEach((file) => {
          if (file instanceof File) {
            logoFilesToUpload.push(file);
          } else {
            existingLogoFiles.push(file);
          }
        });
      }

      if (existingLogoFiles.length > 0) {
        projectData.company_logo = existingLogoFiles;
      }

      const project = await createPremiumProject(projectData);
      if (logoFilesToUpload.length > 0 && project?._id) {
        try {
          const formData = new FormData();
          logoFilesToUpload.forEach((file) => {
            formData.append("files", file);
          });
          const uploadResponse = await uploadFile(
            project._id,
            formData,
            () => {},
          ); //
          const uploadedFiles = uploadResponse?.files || [];
          const logoToSet =
            uploadedFiles.length > 0 ? uploadedFiles : existingLogoFiles;
          if (logoToSet.length > 0) {
            await updateProject(project._id, {
              legal_company_name: projectData.legal_company_name, // Required field
              company_logo: logoToSet,
            });
          }
        } catch (error) {
          console.error("Failed to upload logo:", error);
          notification.warning({
            message: "Project created but logo upload failed",
            description: "You can update the logo later.",
          });
        }
      } else if (existingLogoFiles.length > 0 && project?._id) {
        await updateProject(project._id, {
          legal_company_name: projectData.legal_company_name,
          company_logo: existingLogoFiles,
        });
      }

      return project;
    },
    onSuccess: async (newProject) => {
      notification.success({ message: "Premium project created successfully" });

      // Update the current user data
      const updatedUser = await fetchCurrentUser();
      setCurrentUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));

      // Find the newly created project and set it as selected project
      const newlyCreatedProject = updatedUser.projects.find(
        (p) => p.project_id === newProject?._id,
      );

      if (newlyCreatedProject) {
        setSelectedProject(newlyCreatedProject);
      }

      // Invalidate the query cache
      queryClient.invalidateQueries(["currentUser"]);

      form.resetFields();
      onClose();
    },
    onError: (error) => {
      notification.error({
        message: "Failed to create project",
        description: error.message,
      });
    },
  });

  const handleClose = () => {
    form.resetFields();
    onClose();
  };

  // Prefill from the last/selected project (company info, address, settings, logo).
  useEffect(() => {
    if (!visible) {
      return;
    }

    const sourceProject = selectedProject || currentUser?.projects?.[0];
    const getSetting = (key, camelKey) =>
      sourceProject?.[key] ??
      sourceProject?.[camelKey] ??
      sourceProject?.settings?.[key] ??
      sourceProject?.settings?.[camelKey];

    const initialValues = {
      country_code: sourceProject?.country_code || "usa",
      street_address: sourceProject?.street_address ?? "",
      city: sourceProject?.city ?? "",
      state: sourceProject?.state ?? "",
      postal_code: sourceProject?.postal_code ?? "",
      rfi_workflow_type: sourceProject?.rfi_workflow_type || "Parallel",
      rfi_sequential_requires_all: normalizeBooleanValue(
        getSetting("rfi_sequential_requires_all", "rfiSequentialRequiresAll")
      ),
      restrict_vendor_visibility: normalizeBooleanValue(
        getSetting("restrict_vendor_visibility", "restrictVendorVisibility")
      ),
      open_submittals_summary_enabled: normalizeBooleanValue(
        getSetting(
          "open_submittals_summary_enabled",
          "openSubmittalsSummaryEnabled"
        )
      ),
      track_material_release: normalizeBooleanValue(
        getSetting("track_material_release", "trackMaterialRelease")
      ),
      include_stamp_page_on_coversheet: normalizeBooleanValue(
        getSetting(
          "include_stamp_page_on_coversheet",
          "includeStampPageOnCoversheet"
        )
      ),
      use_custom_terminology: normalizeBooleanValue(
        getSetting("use_custom_terminology", "useCustomTerminology")
      ),
      table_header_color:
        sourceProject?.table_header_color || "rgb(16, 24, 65)",
      settings: {
        notify_received_from_on_submittal_closed: normalizeBooleanValue(
          getSetting(
            "notify_received_from_on_submittal_closed",
            "notifyReceivedFromOnSubmittalClosed"
          )
        ),
        submittal_notify_ball_in_court_only: normalizeBooleanValue(
          getSetting(
            "submittal_notify_ball_in_court_only",
            "submittalNotifyBallInCourtOnly"
          )
        ),
        debounce_notification_emails: normalizeBooleanValue(
          getSetting(
            "debounce_notification_emails",
            "debounceNotificationEmails"
          ) ??
            getSetting(
              "rfi_debounce_notification_emails",
              "rfiDebounceNotificationEmails"
            )
        ),
        disable_all_email_notifications: normalizeBooleanValue(
          getSetting(
            "disable_all_email_notifications",
            "disableAllEmailNotifications"
          )
        ),
        do_not_send_reminder_emails_on_weekends: normalizeBooleanValue(
          getSetting(
            "do_not_send_reminder_emails_on_weekends",
            "doNotSendReminderEmailsOnWeekends"
          )
        ),
        time_change_orders: normalizeBooleanValue(
          getSetting("time_change_orders", "timeChangeOrders")
        ),
      },
    };

    if (sourceProject?.legal_company_name) {
      initialValues.legal_company_name = sourceProject.legal_company_name;
    }
    if (sourceProject?.legal_company_address) {
      initialValues.legal_company_address = sourceProject.legal_company_address;
    }
    if (sourceProject?.legal_company_phone) {
      initialValues.legal_company_phone = formatPhoneNumber(
        sourceProject.legal_company_phone
      );
    }

    const defaultLogo =
      sourceProject?.company_logo?.[0] ||
      getDefaultCompanyLogo(currentUser?.projects);
    if (defaultLogo) {
      initialValues.company_logo = [defaultLogo];
    }

    form.setFieldsValue(initialValues);
  }, [visible, currentUser, selectedProject, form]);

  return (
    <Modal
      title="Create Project"
      open={visible}
      onCancel={handleClose}
      footer={null}
      centered
    >
      <Form
        form={form}
        onFinish={(values) => createPremiumProjectMutation.mutate(values)}
        layout="vertical"
      >
        <Form.Item
          label="Project Name"
          name="name"
          rules={[
            { required: true, message: "Please input the project name!" },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="Description" name="description">
          <Input />
        </Form.Item>
        <Form.Item
          label="Legal Company Name"
          name="legal_company_name"
          rules={[
            { required: true, message: "Please input the legal company name!" },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item label="Company Address" name="legal_company_address">
          <Input />
        </Form.Item>
        <Form.Item label="Company Phone" name="legal_company_phone">
          <Input
            placeholder="e.g. (123) 456-7890"
            onKeyDown={handlePhoneNumberKeyDown}
            onChange={(e) => {
              const formatted = formatPhoneNumber(e.target.value);
              form.setFieldsValue({ legal_company_phone: formatted });
            }}
          />
        </Form.Item>

        <ProjectAddressFormFields form={form} />

        {/* Hidden: not shown in UI; copied from last project and submitted on create. */}
        <Form.Item name="use_custom_terminology" hidden>
          <Input type="hidden" />
        </Form.Item>

        <Form.Item
          label="RFI Workflow Type"
          name="rfi_workflow_type"
          initialValue="Parallel"
        >
          <Select>
            <Select.Option value="Parallel">Parallel</Select.Option>
            <Select.Option value="Sequential">Sequential</Select.Option>
          </Select>
        </Form.Item>

        <div className="project-settings-grid">
          <ProjectSettingToggle
            name="rfi_sequential_requires_all"
            title="RFI Sequential Requires All"
            description="Applicable for sequential RFI review."
          />
          <ProjectSettingToggle
            name="restrict_vendor_visibility"
            title="Restrict Vendor Visibility"
            description="Subcontractors and consultants only see items assigned to them."
          />
          <ProjectSettingToggle
            name="open_submittals_summary_enabled"
            title="Weekly Open Submittal Summary"
            description="Email a PDF report to project owners and managers every Friday."
          />
          <ProjectSettingToggle
            name="track_material_release"
            title="Track Material Release"
            description="Show released-on and estimated-delivery dates on submittals."
          />
          <ProjectSettingToggle
            name="include_stamp_page_on_coversheet"
            title="Include Stamp Page on Coversheet"
            description="Add a second coversheet page with a consultant comments box."
          />
          <ProjectSettingToggle
            name={["settings", "notify_received_from_on_submittal_closed"]}
            title="Notify Subcontractor on Submittal/RFI Closed"
            description="Email the subcontractor when a submittal or RFI changes from Open to Closed."
          />
          <ProjectSettingToggle
            name={["settings", "submittal_notify_ball_in_court_only"]}
            title="Submittal Email: Ball in Court Only"
            description="Email only the person taking action and current ball-in-court users."
          />
          <ProjectSettingToggle
            name={["settings", "debounce_notification_emails"]}
            title="Delay Notification Emails (10 min)"
            description="Wait 10 minutes before sending RFI and submittal create/update emails so you can fix mistakes. Further edits reset the timer; the email uses the latest state."
          />
          <ProjectSettingToggle
            name={["settings", "disable_all_email_notifications"]}
            title="Turn Off All Email Notifications"
            description={
              <>
                When this setting is on, individual email notification switches
                will not work even if they are kept on. Only login codes,
                drawing upload notifications, and custom report emails will
                still be sent.
              </>
            }
          />
          <ProjectSettingToggle
            name={["settings", "do_not_send_reminder_emails_on_weekends"]}
            title="Do Not Send Reminder Emails on Weekends"
            description="Skip RFI and submittal reminder emails on Saturday and Sunday"
          />
          <ProjectSettingToggle
            name={["settings", "time_change_orders"]}
            title="Time Change Orders"
            description="Track contract duration and extra days on change orders (no-charge time extensions)."
          />
        </div>

        <Form.Item name="company_logo" label="Company Logo" className="w-full">
          <AttachmentsSection
            projectId={null}
            value={form.getFieldValue("company_logo")}
            onChange={(value) => form.setFieldValue("company_logo", value)}
            multiple={false}
            accept="image/*"
            label="Add Company Logo"
            title="Upload Company Logo"
            description="Upload your company logo here. Only image files are accepted. If you have a logo from a previous project, it will be automatically added as default."
            disableAutoUpload={true}
          />
        </Form.Item>

        <Form.Item name="table_header_color" label="Project Theme Color">
          <ColorPicker
            format="rgb"
            showText
            defaultValue="rgb(16, 24, 65)"
            onChange={(color) => {
              const rgb = color.toRgb();
              form.setFieldValue(
                "table_header_color",
                `rgb(${rgb.r}, ${rgb.g}, ${rgb.b})`,
              );
            }}
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={createPremiumProjectMutation.isPending}
          >
            Create Project
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default CreatePremiumProjectModal;
